import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../components/shared/Layout';
import api, { matchAPI } from '../../utils/api';
import useIsMobile from '../../utils/useIsMobile';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace('/api', '');

const STATUS_COLORS = {
  pending:     { bg: '#f3f4f6', color: '#6b7280', label: 'Bekliyor' },
  shortlisted: { bg: '#fef9c3', color: '#a16207', label: 'Ön Listede' },
  invited:     { bg: '#dbeafe', color: '#1d4ed8', label: 'Davet Edildi' },
  accepted:    { bg: '#dcfce7', color: '#16a34a', label: 'Kabul Edildi' },
  rejected:    { bg: '#fef2f2', color: '#dc2626', label: 'Reddedildi' },
};

const ScoreCircle = ({ score }) => {
  const color = score >= 75 ? '#16a34a' : score >= 50 ? '#d97706' : '#dc2626';
  const r = 22, c = 2 * Math.PI * r;
  return (
    <div style={{ textAlign: 'center', flexShrink: 0 }}>
      <svg width="60" height="60" viewBox="0 0 60 60">
        <circle cx="30" cy="30" r={r} fill="none" stroke="#f3f4f6" strokeWidth="4" />
        <circle cx="30" cy="30" r={r} fill="none" stroke={color} strokeWidth="4"
          strokeDasharray={c} strokeDashoffset={c - (score / 100) * c}
          strokeLinecap="round" transform="rotate(-90 30 30)" />
        <text x="30" y="35" textAnchor="middle" fontSize="13" fontWeight="600" fill={color}>{Math.round(score)}%</text>
      </svg>
      <div style={{ fontSize: 11, color: '#6b7280' }}>uyum</div>
    </div>
  );
};

const ScoreBreakdown = ({ breakdown }) => {
  if (!breakdown) return null;
  let data;
  try { data = typeof breakdown === 'string' ? JSON.parse(breakdown) : breakdown; } catch { return null; }
  const items = Object.entries(data).filter(([, v]) => v != null);
  if (!items.length) return null;
  const labels = { skills: 'Beceriler', gpa: 'GPA', department: 'Bölüm', language: 'Dil', overall: 'Genel' };
  return (
    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {items.map(([k, v]) => (
        <span key={k} style={{ fontSize: 11, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '2px 8px', color: '#374151' }}>
          {labels[k] || k}: <strong>{Math.round(v)}%</strong>
        </span>
      ))}
    </div>
  );
};

const CompanyCandidates = () => {
  const { positionId } = useParams();
  const isMobile = useIsMobile();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minScore, setMinScore] = useState(0);
  const [message, setMessage] = useState('');
  const [selected, setSelected] = useState(new Set());

  // Aksiyon modalı
  const [actionModal, setActionModal] = useState(null); // { candidate, type: 'invite'|'reject'|'interview' }
  const [actionNote, setActionNote] = useState('');
  const [interviewDate, setInterviewDate] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Karşılaştırma modalı
  const [compareModal, setCompareModal] = useState(false);
  const [noteModal, setNoteModal] = useState(null); // { candidate, note, tags }

  const load = (score = minScore) => {
    setLoading(true);
    matchAPI.getCandidates(positionId, { minScore: score })
      .then(r => { setCandidates(r.data.candidates); setSelected(new Set()); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [positionId]);

  const showMsg = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 4000); };

  const openAction = (candidate, type) => {
    setActionModal({ candidate, type });
    setActionNote('');
    setInterviewDate('');
  };

  const closeAction = () => setActionModal(null);

  const confirmAction = async () => {
    if (!actionModal) return;
    setActionLoading(true);
    const { candidate, type } = actionModal;
    try {
      if (type === 'invite') {
        await matchAPI.updateStatus(candidate.application_id, 'invited', {
          interview_date: interviewDate || undefined,
        });
        showMsg(`${candidate.first_name} davet edildi`);
      } else if (type === 'reject') {
        await matchAPI.updateStatus(candidate.application_id, 'rejected', {
          rejection_reason: actionNote || undefined,
        });
        showMsg(`${candidate.first_name} reddedildi`);
      } else if (type === 'shortlist') {
        await matchAPI.updateStatus(candidate.application_id, 'shortlisted');
        showMsg(`${candidate.first_name} ön listeye alındı`);
      }
      closeAction();
      load();
    } catch { showMsg('İşlem başarısız'); }
    finally { setActionLoading(false); }
  };

  const handleBulkInvite = async () => {
    if (!selected.size) return;
    const ids = [...selected];
    try {
      await matchAPI.bulkUpdateStatus(ids, 'invited');
      showMsg(`${ids.length} aday davet edildi`);
      load();
    } catch { showMsg('Toplu davet başarısız'); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === candidates.length) setSelected(new Set());
    else setSelected(new Set(candidates.map(c => c.application_id)));
  };

  const compareList = candidates.filter(c => selected.has(c.application_id)).slice(0, 2);

  return (
    <Layout>
      <div style={{ maxWidth: 820 }}>
        {/* Başlık */}
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>Aday Listesi</h1>
          <p style={{ color: '#6b7280', fontSize: 14, marginTop: 4 }}>
            {candidates.length} aday — zorunlu kriterleri geçenler
          </p>
        </div>

        {/* Eşleştirme bilgilendirmesi — eşleştirme sistem yöneticisi tarafından yapılır */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '12px 16px', borderRadius: 8, marginBottom: 16,
          background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', fontSize: 13, lineHeight: 1.5
        }}>
          <span style={{ fontSize: 16, lineHeight: 1.2 }}>ℹ️</span>
          <span>
            Eşleştirme <strong>sistem yöneticisi</strong> tarafından yapılmaktadır.
            Uygun adaylar belirlendiğinde bu listede otomatik olarak görüntülenecektir.
          </span>
        </div>

        {message && (
          <div style={{
            padding: '10px 16px', borderRadius: 8, marginBottom: 16,
            background: message.includes('başarısız') ? '#fef2f2' : '#f0fdf4',
            color: message.includes('başarısız') ? '#dc2626' : '#16a34a', fontSize: 13
          }}>{message}</div>
        )}

        {/* Araç çubuğu */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          {[0, 50, 75].map(v => (
            <button key={v} onClick={() => { setMinScore(v); load(v); }} style={{
              padding: '6px 16px', borderRadius: 20, cursor: 'pointer', fontSize: 13,
              border: `1.5px solid ${minScore === v ? '#2563eb' : '#e5e7eb'}`,
              background: minScore === v ? '#eff6ff' : '#fff',
              color: minScore === v ? '#1d4ed8' : '#374151', fontWeight: minScore === v ? 500 : 400
            }}>
              {v === 0 ? 'Tümü' : `≥%${v}`}
            </button>
          ))}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {selected.size >= 2 && (
              <button onClick={() => setCompareModal(true)} style={{
                padding: '6px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                background: '#f5f3ff', border: '1px solid #c4b5fd', color: '#7c3aed', fontWeight: 500
              }}>
                Karşılaştır ({selected.size})
              </button>
            )}
            {selected.size > 0 && (
              <button onClick={handleBulkInvite} style={{
                padding: '6px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                background: '#eff6ff', border: '1px solid #93c5fd', color: '#1d4ed8', fontWeight: 500
              }}>
                Toplu Davet Et ({selected.size})
              </button>
            )}
          </div>
        </div>

        {/* Liste başlığı / Tümünü seç */}
        {candidates.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '0 4px' }}>
            <input type="checkbox"
              checked={selected.size === candidates.length && candidates.length > 0}
              onChange={selectAll}
              style={{ width: 16, height: 16, cursor: 'pointer' }} />
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              {selected.size > 0 ? `${selected.size} seçili` : 'Tümünü seç'}
            </span>
          </div>
        )}

        {/* Aday listesi */}
        {loading ? (
          <p style={{ color: '#6b7280', padding: '40px 0', textAlign: 'center' }}>Yükleniyor...</p>
        ) : candidates.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#6b7280', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>👥</div>
            <p style={{ fontWeight: 600, fontSize: 16, color: '#111827' }}>Henüz aday yok</p>
            <p style={{ fontSize: 14, marginTop: 6, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
              Eşleştirme sistem yöneticisi tarafından yapılmaktadır. Uygun adaylar
              belirlendiğinde bu listede otomatik olarak görüntülenecektir.
            </p>
          </div>
        ) : (
          candidates.map(c => {
            const st = STATUS_COLORS[c.status] || STATUS_COLORS.pending;
            const isSelected = selected.has(c.application_id);
            return (
              <div key={c.application_id} style={{
                border: `1.5px solid ${isSelected ? '#93c5fd' : '#e5e7eb'}`,
                borderRadius: 12, padding: 20, background: isSelected ? '#f0f9ff' : '#fff',
                display: 'flex', gap: 14, marginBottom: 10,
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                {/* Checkbox */}
                <input type="checkbox" checked={isSelected}
                  onChange={() => toggleSelect(c.application_id)}
                  style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0, marginTop: 4 }} />

                {/* Skor çemberi */}
                <ScoreCircle score={c.match_score || 0} />

                {/* Bilgiler */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 16, color: '#111827' }}>
                        {c.first_name} {c.last_name}
                      </div>
                      <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                        {c.university} • {c.department}{c.grade ? ` • ${c.grade}. sınıf` : ''}
                        {c.gpa ? ` • GPA: ${c.gpa}` : ''}
                        {c.city ? ` • ${c.city}` : ''}
                      </div>
                    </div>
                    <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: st.bg, color: st.color, fontWeight: 500 }}>
                      {st.label}
                    </span>
                  </div>

                  {/* Skor breakdown */}
                  <ScoreBreakdown breakdown={c.score_breakdown} />

                  {/* Beceriler */}
                  {c.skills?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 10 }}>
                      {c.skills.slice(0, 6).map(s => (
                        <span key={s} style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: 11, padding: '2px 8px', borderRadius: 20 }}>{s}</span>
                      ))}
                      {c.skills.length > 6 && <span style={{ fontSize: 11, color: '#9ca3af' }}>+{c.skills.length - 6}</span>}
                    </div>
                  )}

                  {/* Aksiyon butonları */}
                  <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                    {c.cv_file_url && (
                      <button onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const res = await fetch(`${API_BASE}${c.cv_file_url}`, { headers: { Authorization: `Bearer ${token}` } });
                          if (!res.ok) throw new Error('CV alınamadı');
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          window.open(url, '_blank');
                        } catch (e) { alert('CV açılamadı: ' + e.message); }
                      }} style={{
                        padding: '5px 12px', borderRadius: 7, background: '#f9fafb',
                        border: '1px solid #e5e7eb', fontSize: 12, color: '#374151', cursor: 'pointer'
                      }}>CV Gör</button>
                    )}
                    <button onClick={async () => {
                      try {
                        const r = await api.get(`/applications/${c.application_id}/notes`);
                        setNoteModal({ candidate: c, note: r.data?.note || '', tags: r.data?.tags || [] });
                      } catch { setNoteModal({ candidate: c, note: '', tags: [] }); }
                    }} style={{
                      padding: '5px 12px', borderRadius: 7, background: '#fef3c7',
                      border: '1px solid #fcd34d', fontSize: 12, color: '#92400e', cursor: 'pointer', fontWeight: 500
                    }}>📝 Not / Etiket</button>
                    {c.status === 'pending' && (
                      <>
                        <button onClick={() => openAction(c, 'shortlist')} style={{
                          padding: '5px 12px', borderRadius: 7, background: '#fef9c3',
                          border: '1px solid #fde047', fontSize: 12, color: '#a16207', cursor: 'pointer'
                        }}>Ön Listeye Al</button>
                        <button onClick={() => openAction(c, 'invite')} style={{
                          padding: '5px 12px', borderRadius: 7, background: '#eff6ff',
                          border: '1px solid #93c5fd', fontSize: 12, color: '#1d4ed8', cursor: 'pointer', fontWeight: 500
                        }}>Davet Et</button>
                        <button onClick={() => openAction(c, 'reject')} style={{
                          padding: '5px 12px', borderRadius: 7, background: '#fff',
                          border: '1px solid #fca5a5', fontSize: 12, color: '#dc2626', cursor: 'pointer'
                        }}>Reddet</button>
                      </>
                    )}
                    {c.status === 'shortlisted' && (
                      <button onClick={() => openAction(c, 'invite')} style={{
                        padding: '5px 12px', borderRadius: 7, background: '#eff6ff',
                        border: '1px solid #93c5fd', fontSize: 12, color: '#1d4ed8', cursor: 'pointer', fontWeight: 500
                      }}>Davet Et</button>
                    )}
                    {c.interview_date && (
                      <span style={{ fontSize: 12, color: '#6b7280' }}>
                        Mülakat: {new Date(c.interview_date).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}

        {/* Aksiyon Modal */}
        {actionModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 480, maxWidth: '90vw' }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                {actionModal.type === 'invite' ? 'Davet Et' : actionModal.type === 'reject' ? 'Reddet' : 'Ön Listeye Al'}
              </h2>
              <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>
                {actionModal.candidate.first_name} {actionModal.candidate.last_name}
              </p>

              {actionModal.type === 'invite' && (
                <>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                    Mülakat Tarihi (opsiyonel)
                  </label>
                  <input type="datetime-local" value={interviewDate} onChange={e => setInterviewDate(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 16 }} />
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                    Davet Mesajı (opsiyonel)
                  </label>
                  <textarea rows={3} value={actionNote} onChange={e => setActionNote(e.target.value)}
                    placeholder="Öğrenciye özel mesaj..."
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
                </>
              )}

              {actionModal.type === 'reject' && (
                <>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
                    Red Gerekçesi (opsiyonel)
                  </label>
                  <textarea rows={3} value={actionNote} onChange={e => setActionNote(e.target.value)}
                    placeholder="Öğrenciye iletilecek gerekçe..."
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
                </>
              )}

              <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                <button onClick={confirmAction} disabled={actionLoading} style={{
                  flex: 1, padding: '11px 0',
                  background: actionModal.type === 'reject' ? '#dc2626' : '#2563eb',
                  color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer'
                }}>
                  {actionLoading ? 'İşleniyor...' : actionModal.type === 'invite' ? 'Davet Gönder' : actionModal.type === 'reject' ? 'Reddet' : 'Ön Listeye Al'}
                </button>
                <button onClick={closeAction} style={{
                  padding: '11px 20px', background: '#f3f4f6', border: '1px solid #d1d5db',
                  borderRadius: 8, fontSize: 14, cursor: 'pointer'
                }}>İptal</button>
              </div>
            </div>
          </div>
        )}

        {/* Karşılaştırma Modal */}
        {compareModal && compareList.length >= 2 && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 760, maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>Aday Karşılaştırma</h2>
                <button onClick={() => setCompareModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>✕</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
                {compareList.map((c, i) => {
                  const score = c.match_score || 0;
                  const scoreColor = score >= 75 ? '#16a34a' : score >= 50 ? '#d97706' : '#dc2626';
                  return (
                    <div key={c.application_id} style={{ background: '#f9fafb', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
                      <div style={{ textAlign: 'center', marginBottom: 16 }}>
                        <div style={{ fontSize: 32, fontWeight: 700, color: scoreColor }}>%{Math.round(score)}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>uyum skoru</div>
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 15, color: '#111827', textAlign: 'center', marginBottom: 12 }}>
                        {c.first_name} {c.last_name}
                      </div>
                      {[
                        ['Üniversite', c.university],
                        ['Bölüm', c.department],
                        ['Sınıf', c.grade ? `${c.grade}. Sınıf` : '—'],
                        ['GPA', c.gpa || '—'],
                        ['Şehir', c.city || '—'],
                      ].map(([label, val]) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e5e7eb', fontSize: 13 }}>
                          <span style={{ color: '#6b7280' }}>{label}</span>
                          <span style={{ color: '#111827', fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{val}</span>
                        </div>
                      ))}
                      <ScoreBreakdown breakdown={c.score_breakdown} />
                      {c.skills?.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Beceriler</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {c.skills.slice(0, 8).map(s => (
                              <span key={s} style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: 11, padding: '2px 7px', borderRadius: 20 }}>{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {c.cv_file_url && (
                        <button onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const res = await fetch(`${API_BASE}${c.cv_file_url}`, { headers: { Authorization: `Bearer ${token}` } });
                            if (!res.ok) throw new Error('CV alınamadı');
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                          } catch (e) { alert('CV açılamadı: ' + e.message); }
                        }} style={{
                          display: 'block', width: '100%', marginTop: 14, textAlign: 'center', padding: '8px', background: '#fff',
                          border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, color: '#374151', cursor: 'pointer'
                        }}>CV Gör</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}


      {noteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 26, width: 520, maxWidth: '90vw' }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Aday Not / Etiket</h2>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>{noteModal.candidate.first_name} {noteModal.candidate.last_name}</p>
            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 6 }}>Etiketler</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {['Görüşüldü','Güçlü Aday','Geri Bildir','Tekrar Değerlendir','Uygun Değil','Favori'].map(tag => {
                const active = noteModal.tags.includes(tag);
                return (
                  <span key={tag} onClick={() => setNoteModal(m => ({ ...m, tags: active ? m.tags.filter(t => t !== tag) : [...m.tags, tag] }))}
                    style={{ cursor: 'pointer', fontSize: 12, padding: '4px 10px', borderRadius: 20, border: '1px solid', borderColor: active ? '#2563eb' : '#d1d5db', background: active ? '#eff6ff' : '#fff', color: active ? '#1d4ed8' : '#6b7280' }}>
                    {active ? '✓ ' : ''}{tag}
                  </span>
                );
              })}
            </div>
            <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 6 }}>Özel Not</label>
            <textarea rows={5} value={noteModal.note} onChange={e => setNoteModal(m => ({ ...m, note: e.target.value }))}
              placeholder="Aday hakkında notlarınız (sadece şirketinizin göreceği)"
              style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, resize: 'vertical', boxSizing: 'border-box', marginBottom: 18 }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setNoteModal(null)} style={{ padding: '9px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer' }}>İptal</button>
              <button onClick={async () => {
                try {
                  await api.put(`/applications/${noteModal.candidate.application_id}/notes`, { note: noteModal.note, tags: noteModal.tags });
                  setNoteModal(null);
                  showMsg('Not kaydedildi');
                } catch { alert('Kaydedilemedi'); }
              }} style={{ padding: '9px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 500 }}>Kaydet</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
};

export default CompanyCandidates;
